<script lang="ts">
  import { browser } from '$app/environment';
  import { onDestroy, onMount, setContext } from 'svelte';
  import { writable, type Writable } from 'svelte/store';
  import { twMerge } from 'tailwind-merge';
  import IconCircleButton from '../IconCircleButton.svelte';
  import type { CarouselContext } from './types';

  export let desktopControlsPosition: 'side' | 'bottom' = 'bottom';

  let carouselElement: HTMLDivElement;
  let observer: IntersectionObserver;

  // Stores to manage slides and the active slide
  const slideIds: Writable<string[]> = writable([]);
  const activeSlideId: Writable<string> = writable('');
  let activeIndex = 0;

  // Define the context type here or import it if defined externally
  setContext<CarouselContext>('carousel-context', { activeSlideId, slideIds });

  onMount(() => {
    if (browser) {
      slideIds.subscribe((ids) => {
        if (observer) {
          observer.disconnect(); // Ensure to disconnect previous observers
        }

        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio >= 0.5) {
                activeSlideId.set(entry.target.getAttribute('id') || '');
              }
            });
          },
          {
            root: carouselElement,
            threshold: 0.5
          }
        );

        ids.forEach((id) => {
          const slideElement = document.getElementById(id);

          if (slideElement) {
            observer.observe(slideElement);
          }
        });
      });
    }
  });

  onDestroy(() => {
    observer?.disconnect();
  });

  $: activeIndex = $slideIds.indexOf($activeSlideId);

  const scrollToSlide = (direction: 'next' | 'prev') => {
    const increment = direction === 'next' ? 1 : -1;
    const newIndex = activeIndex + increment;

    if (newIndex >= 0 && newIndex < $slideIds.length) {
      const toSlideId = $slideIds[newIndex];
      const toSlideEl = document.getElementById(toSlideId);

      if (toSlideEl) {
        toSlideEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };
</script>

<div
  class={twMerge('relative flex flex-col', $$restProps.class)}
  role="region"
  aria-live="polite"
  aria-label="Carousel"
>
  <div class="relative h-full">
    <div
      bind:this={carouselElement}
      class={twMerge(
        '_no-scrollbar relative flex h-full grow snap-x snap-mandatory gap-x-8 overflow-x-auto whitespace-normal'
      )}
    >
      <slot />
    </div>

    {#if desktopControlsPosition === 'side'}
      <div class="absolute bottom-0 top-0 flex items-center xl:-left-24">
        <IconCircleButton
          name="arrow-left"
          size="lg"
          disabled={activeIndex === 0}
          on:click={() => scrollToSlide('prev')}
          class="hidden xl:inline-flex"
          ariaLabel="Go to previous slide in carousel"
        />
      </div>

      <div class="absolute bottom-0 top-0 flex items-center xl:-right-24">
        <IconCircleButton
          name="arrow-right"
          size="lg"
          data-disabled={activeIndex}
          disabled={activeIndex === $slideIds.length - 1}
          on:click={() => scrollToSlide('next')}
          class="hidden xl:inline-flex"
          ariaLabel="Go to next slide in carousel"
        />
      </div>
    {/if}
  </div>

  {#if $slideIds.length > 0}
    <div class="mt-7 flex items-center justify-center gap-x-6 md:mt-8 md:gap-x-8 lg:mt-9">
      <IconCircleButton
        name="arrow-left"
        size="lg"
        disabled={activeIndex === 0}
        on:click={() => scrollToSlide('prev')}
        class={desktopControlsPosition === 'side' && 'xl:hidden'}
        ariaLabel="Go to previous slide in carousel"
      />

      <ul
        class="flex h-3 items-center justify-center gap-x-2.5"
        aria-label="Carousel Pagination Indicators"
      >
        {#each $slideIds as slideId}
          <li
            class={twMerge(
              'h-2 w-2 rounded-full bg-blue-950 transition-all duration-200',
              slideId === $activeSlideId && 'h-3 w-3 bg-blue'
            )}
            aria-selected={slideId === $activeSlideId}
            role="tab"
          />
        {/each}
      </ul>

      <IconCircleButton
        name="arrow-right"
        size="lg"
        disabled={activeIndex === $slideIds.length - 1}
        on:click={() => scrollToSlide('next')}
        class={desktopControlsPosition === 'side' && 'xl:hidden'}
        ariaLabel="Go to next slide in carousel"
      />
    </div>
  {/if}
</div>
